import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Plus } from "lucide-react";
import { NextGenLayoutContext } from "~/components/ManifoldNextGenLayout";
import { SkillEditorMode, type SkillFormData } from "~/components/assistant/SkillEditorForm";
import SkillEditorModal from "~/components/assistant/SkillEditorModal";
import { SkillRow } from "~/components/assistant/SkillRow";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import { Switch } from "~/components/ui/switch";
import useToast from "~/components/ui/toast";
import useNextGenHeader from "~/contexts/nextgen-header";
import useUser from "~/contexts/user";
import { useSkillMutations } from "~/pages/manifold-nextgen/components/conversations/skills/useSkillMutations";
import { SettingsRow } from "~/pages/manifold-nextgen/components/settings/SettingsRow";
import { SettingsSection } from "~/pages/manifold-nextgen/components/settings/SettingsSection";
import { type PartialSkill, type Skill } from "~/services/skills";
import { getAvatarInitials } from "~/utils/strings";
import { useUserSkills } from "./useUserSkills";

type SkillDialogState = { kind: "none" } | { kind: "editor"; skill?: Skill };

export const UserProfile = () => {
  const user = useUser();
  const navigate = useNavigate();
  const { isDark, onToggleDark, onSwitchToClassic } = useOutletContext<NextGenLayoutContext>();
  const { showSuccess, showError } = useToast();

  useNextGenHeader({ title: "Profile", onBack: () => navigate(-1) });

  const displayName = user?.name ?? user?.email ?? "";
  const initials = displayName ? getAvatarInitials(displayName) : "";

  const { skills, isLoading: skillsLoading } = useUserSkills(user?.email);
  const { createUserSkill, updateSkill, deleteSkill, getSkill } = useSkillMutations();

  const [skillDialog, setSkillDialog] = useState<SkillDialogState>({ kind: "none" });
  const [deleteTarget, setDeleteTarget] = useState<PartialSkill | null>(null);
  const [isSavingSkill, setIsSavingSkill] = useState(false);
  const [isDeletingSkill, setIsDeletingSkill] = useState(false);

  const handleEditSkill = async (skillId: string) => {
    try {
      const skill = await getSkill(skillId);
      setSkillDialog({ kind: "editor", skill });
    } catch {
      showError("Unable to load skill for editing");
    }
  };

  const handleSkillSubmit = async (data: SkillFormData) => {
    setIsSavingSkill(true);
    try {
      if (skillDialog.kind === "editor" && skillDialog.skill) {
        await updateSkill(skillDialog.skill.id, {
          description: data.description,
          content: data.content,
        });
        showSuccess(`"${skillDialog.skill.name}" updated`);
      } else {
        await createUserSkill(data);
        showSuccess(`"${data.name}" added`);
      }
      setSkillDialog({ kind: "none" });
    } catch (e) {
      showError(e instanceof Error ? e.message : "Unable to save skill");
    } finally {
      setIsSavingSkill(false);
    }
  };

  const handleDeleteSkillConfirm = async () => {
    if (!deleteTarget) {
      return;
    }
    setIsDeletingSkill(true);
    try {
      await deleteSkill(deleteTarget);
      showSuccess(`"${deleteTarget.name}" removed`);
      setDeleteTarget(null);
    } catch {
      showError("Unable to remove skill");
    } finally {
      setIsDeletingSkill(false);
    }
  };

  const skillEditorInitialData =
    skillDialog.kind === "editor" && skillDialog.skill
      ? {
          name: skillDialog.skill.name,
          description: skillDialog.skill.description ?? "",
          content: skillDialog.skill.content,
        }
      : undefined;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6 p-6">
      <SettingsSection title="Personal information">
        <div className="flex items-center gap-4 px-4 py-4">
          <Avatar className="size-[56px] shrink-0">
            <AvatarImage src={user?.picture} alt={displayName} />
            <AvatarFallback className="text-lg">{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">{displayName}</p>
            <p className="truncate text-xs text-muted-foreground">{user?.email ?? ""}</p>
          </div>
        </div>
      </SettingsSection>

      <SettingsSection title="Appearance">
        <SettingsRow title="Dark mode" description="Use the dark color theme across Manifold.">
          <Switch checked={isDark} onCheckedChange={onToggleDark} aria-label="Dark mode" />
        </SettingsRow>
        {onSwitchToClassic && (
          <SettingsRow
            title="Classic look"
            description="Switch back to the previous Manifold interface."
          >
            <Button variant="outline" onClick={onSwitchToClassic}>
              Switch to classic look
            </Button>
          </SettingsRow>
        )}
      </SettingsSection>

      <SettingsSection
        title="Skills"
        description="These skills are available in all your conversations."
        headerAction={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSkillDialog({ kind: "editor" })}
            className="text-primary hover:text-primary/80"
          >
            <Plus />
            Add skill
          </Button>
        }
      >
        {skillsLoading ? (
          <div className="flex flex-col gap-2 p-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : skills.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <p className="text-sm text-muted-foreground">No skills yet.</p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {skills.map((skill) => (
              <SkillRow
                key={skill.id}
                skill={skill}
                canEdit
                onEdit={() => handleEditSkill(skill.id)}
                onDelete={() => setDeleteTarget(skill)}
              />
            ))}
          </ul>
        )}
      </SettingsSection>

      <SkillEditorModal
        open={skillDialog.kind === "editor"}
        onClose={() => setSkillDialog({ kind: "none" })}
        onSubmit={handleSkillSubmit}
        loading={isSavingSkill}
        mode={
          skillDialog.kind === "editor" && skillDialog.skill
            ? SkillEditorMode.EDIT
            : SkillEditorMode.CREATE
        }
        initialData={skillEditorInitialData}
      />

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && !isDeletingSkill && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this skill?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && <>&ldquo;{deleteTarget.name}&rdquo; will be permanently deleted.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingSkill}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSkillConfirm}
              disabled={isDeletingSkill}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeletingSkill ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
